import React, { useEffect } from 'react';
import { setPageMeta } from '../lib/seo';

const policies = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'Last updated: February 8, 2026',
    sections: [
       {
        html: [
          'This Privacy Policy explains how <a href="https://www.elorimi.com/" target="_blank" rel="noreferrer">Elorimi</a> collects, uses, and protects your personal information when you use our Cloud PC or Cloud VPS services.'
        ]
      },
       {
        heading: '1. Company / Data Controller Information',
        list: [
          'Legal entity name: -',
          'Registration number: -',
          'Registered address: -',
          'Email: info@elorimi.com'
        ],
        text: [
          'Where required by law, the entity listed above is the “data controller” (or equivalent term) responsible for personal data processed under this Policy.'
        ]

      },
      {
        heading: '2. Scope',
        text: [
          'This Policy applies to:'
        ],
        list: [
          'The Website and Service (including Cloud PC and Cloud VPS offerings),',
          'Customer support and communications,',
          'Billing and account administration,',
          'Marketing activities (where permitted and/or with consent).'
        ],
        textAfter: [
          'This Policy does not apply to third-party websites or services that may be linked from the Website.'
        ]

      },
       {
        heading: '3. Personal Data We Collect',
        text: ['We may collect the following categories of personal data:'],
        blocks: [
          {
            subheading: '3.1 Data you provide directly',
            list: [
              'Account data: name, email address, username, password (hashed), profile details.',
              'Billing data: billing name, billing address, tax/VAT ID (if provided), payment-related metadata (we do not typically store full card details if handled by a payment provider).',
              'Communications: messages to support, emails, feedback, and any information you choose to provide.'
            ]
          },
          {
            subheading: '3.2 Data collected automatically',
            list: [
              'Device and connection data: IP address, browser type, device identifiers, operating system, language settings.',
              'Usage data: pages visited, features used, session duration, timestamps, referring URLs, clicks and interactions.',
              'Security data: login attempts, authentication events, fraud signals, and risk indicators.'
            ]
          },
          {
            subheading: '3.3 Service and infrastructure logs',
            text: ['When you use Cloud PCs / VPS, our systems may generate operational logs such as:'],
            list: [
              'Session start/end times, uptime, region/zone, instance identifiers,',
              'Network and performance telemetry (e.g., CPU/RAM usage levels, disk I/O, bandwidth metrics),',
              'System-level events needed for service reliability and abuse prevention.'
            ]
          }
        ],
        textAfter: [
          'We do not intentionally collect the contents of your private files or communications inside your Cloud PC/VPS, unless you explicitly share them with support or required for troubleshooting with your permission (where feasible).'
        ]
      },
      {
        heading: '4. Why We Process Personal Data (Purposes)',
        text: ['We process personal data for:'],
        list: [
          'Providing the Service and operating the platform (account creation, provisioning instances, delivering access details),',
          'Authentication and security (preventing fraud, abuse, and unauthorized access),',
          'Payment processing, invoicing, accounting, and tax compliance,',
          'Customer support and service communications,',
          'Improving and maintaining performance, reliability, and user experience,',
          'Legal compliance and enforcing our Terms of Service,',
          'Marketing (only where permitted by law and/or with your consent, and with opt-out options).'
        ]
      },
      {
        heading: '5. Legal Bases (Where Applicable)',
        text: ['Depending on your jurisdiction, we rely on one or more of the following legal bases:'],
        list: [
          'Contract necessity (to provide the Service),',
          'Legitimate interests (security, fraud prevention, service improvement),',
          'Legal obligation (accounting, tax, compliance requests),',
          'Consent (cookies/marketing where required).'
        ]
      },
       {
        heading: '6. How We Share Personal Data',
        text: ['We may share personal data with:'],
        blocks: [
          {
            subheading: '6.1 Service providers (processors)',
            list: [
              'Hosting/infrastructure providers and data centers (to run instances),',
              'Payment processors (to handle payments),',
              'Analytics and performance monitoring providers,',
              'Support tools and ticketing systems,',
              'Email/SMS providers.'
            ],
            text: ['They are permitted to process personal data only to provide services to us and must protect it.']
          },

          {
            subheading: '6.2 Legal and safety reasons',
            text: ['We may disclose information if required by law, court order, or to:'],
            list: [
              'Investigate fraud/security incidents,',
              'Protect users, the public, and our rights,',
              'Enforce our Terms.'
            ]
          },
          {
            subheading: '6.3 Business transfers',
            text: ['If we undergo a merger, acquisition, reorganization, or sale of assets, personal data may be transferred as part of that transaction, subject to appropriate protections.'],
            list: [
              'Session start/end times, uptime, region/zone, instance identifiers,',
              'Network and performance telemetry (e.g., CPU/RAM usage levels, disk I/O, bandwidth metrics),',
              'System-level events needed for service reliability and abuse prevention.'
            ]
          }
        ]
      },
       {
        heading: '7. International Data Transfers',
        text: ['Your data may be processed in countries other than your own, including where our providers operate. Where required, we use safeguards such as contractual clauses and security measures to protect international transfers.']
      },
      {
        heading: '8. Data Retention',
        blocks: [
          {
            text: ['We retain personal data only as long as necessary for:'],
            list: [
              'Providing the Service,',
              'Meeting legal/accounting obligations,',
              'Resolving disputes,',
              'Enforcing agreements,',
              'Security and abuse prevention.'
            ]
          },
          {
            text: ['Typical retention periods may include:'],
            list: [
              'Account data: for the life of the account plus a limited period after deletion,',
              'Billing records: as required by applicable tax/accounting laws,',
              'Security logs: retained for a reasonable period to detect and prevent abuse.'
            ]
          }
        ]
      },
      {
        heading: '9. Security Measures',
        blocks: [
          {
            text: ['We implement administrative, technical, and physical safeguards, which may include:'],
            list: [
              'Encryption in transit (TLS) and, where appropriate, encryption at rest,',
              'Meeting legal/accounting obligations,',
              'Access controls and least-privilege policies,',
              'Monitoring, logging, and anomaly detection,',
              'Backups and disaster recovery practices.'
            ]
          },
          {
            text: ['No online service can be guaranteed 100% secure. You are responsible for keeping your credentials confidential.']
          }
        ]
      },
       {
        heading: '10. Your Rights',
        blocks: [
          {
           text: ['Depending on your jurisdiction, you may have rights to:'],
          list: [
              'Access your personal data,',
              'Correct inaccurate data,',
              'Delete data (subject to legal obligations),',
              'Restrict or object to certain processing,',
              'Data portability,',
              'Withdraw consent (where applicable),',
              'Lodge a complaint with a data protection authority.'
          ]
          },
          {
            html: ['To exercise rights, contact: <a href="mailto:info@elorimi.com">info@elorimi.com</a>.']
          }
        ]
      },
      {
        heading: '11. Cookies and Tracking',
        html: ['We use cookies and similar technologies. See our <a href="/cookies">Cookie Policy</a> below for details, including how to manage preferences.']
      },
      {
        heading: '12. Children’s Privacy',
        text: ['The Service is not intended for children. We do not knowingly collect personal data from minors. If you believe a minor provided us data, contact us to remove it.']
      },
      {
        heading: '13. Changes to This Policy',
        text: ['We may update this Policy periodically. We will post changes on the Website and update the “Last updated” date. If changes are material, we may provide additional notice.']
      },
       {
        heading: '14. Contact',
        html: ['For privacy questions or requests: <a href="mailto:info@elorimi.com">info@elorimi.com</a>']
      }

    ]
  },
  terms: {
    title: 'Terms of Service',
    updated: 'Last updated: February 8, 2026',
    sections: [
      {
        html: [
          'These Terms of Service (“Terms”) govern your use of Elorimi and <a href="https://www.elorimi.com/" target="_blank" rel="noreferrer">Elorimi</a> (the “Service”). By accessing or using the Service, you agree to these Terms.'
        ]
      },
      {
        heading: '1. Company Information',
        list: [
          'Legal entity name: -',
          'Registration number: -',
          'Registered address: -',
          'Email: info@elorimi.com'
        ]
      },
      {
        heading: '2. Definitions',
        list: [
          '“Cloud PC”: a remote virtual desktop environment provided for your use.',
          '“Cloud VPS”: a virtual private server environment (Linux/Windows) provided for your use.',
          '“Session”: a time-bound period of access you purchase or receive.',
          '“Content”: any data, files, software, or materials you upload, install, create, or process using the Service.'
        ]
      },
      {
        heading: '3. Eligibility and Accounts',
        blocks: [
          {
            text: ['You must be legally able to form a contract. You agree to:'],
            list: [
              'Provide accurate account information,',
              'Keep credentials confidential,',
              'Be responsible for all activity under your account.'
            ]
          },
          {
            text: ['We may suspend or terminate access for security, compliance, or policy violations.']
          }
        ]
      },
       {
        heading: '4. Service Description',
        blocks: [
          {
            text: ['Elorimi provides cloud-based computing resources on a pay-per-use or subscription basis (as offered). The Service may include:'],
            list: [
              'Provisioning virtual machines,',
              'Access credentials and remote access methods,',
              'Network connectivity and allocated storage.'
            ]
          },
          {
            text: ['No guarantee of continuous availability is provided unless a separate written SLA is offered.']
          }
        ]
      },
      {
        heading: '5. Acceptable Use Policy (AUP)',
        blocks: [
          {
            text: ['You agree not to use the Service for:'],
            list: [
              'Illegal activities, or to facilitate illegal activities,',
              'Malware, hacking, phishing, credential theft, botnets, DDoS, scanning, or exploitation,',
              'Abusing, interfering with, or disrupting networks, systems, or other users,',
              'Hosting or distributing prohibited content (e.g., CSAM, non-consensual intimate content),',
              'Infringing intellectual property rights,',
              'Spamming, unsolicited messaging, or prohibited advertising.'
            ]
          },
          {
            text: ['We may monitor limited technical signals (e.g., traffic anomalies) to protect the Service. We may suspend or terminate accounts involved in abuse.']
          }
        ]
      },
      {
        heading: '6. User Content and Responsibility',
        blocks: [
          {
            text: ['You retain rights to your Content. You are responsible for:'],
            list: [
              'Backups of your Content,',
              'Compliance with laws and licenses for software you install,',
              'Ensuring you have permission for any data you process.'
            ]
          },
          {
            text: ['We are not responsible for loss of Content due to user actions, third-party issues, or force majeure.']
          }
        ]
      },
      {
        heading: '7. Software Licensing',
        text: [
          'You are responsible for complying with software license terms for any software you install or use in your environment. If you use Windows or other licensed software, applicable licensing terms apply.'
        ]
      },
      {
        heading: '8. Security and Access',
        blocks: [
          {
            text: ['You must:'],
            list: [
              'Use strong passwords and protect credentials,',
              'Avoid sharing access with unauthorized users,',
              'Promptly notify us of suspected compromise.'
            ]
          },
          {
            text: ['We may enforce security measures (e.g., connection limits, port restrictions) to protect the platform.']
          }
        ]
      },
         {
        heading: '9. Sessions, Start/End Times, and Availability',
        list: [
              'Access may activate at the scheduled start time and end automatically at the session end time.',
              'Late connection does not necessarily extend session end time.',
              'Maintenance windows or provider outages may occur.'
        ]
      },
      {
        heading: '10. Pricing, Billing, and Taxes',
        blocks: [
          {
            text: ['Prices are shown at checkout unless agreed otherwise. You agree to pay:'],
            list: [
              'Usage fees, subscriptions, add-ons, and applicable taxes.',
              'You are responsible for any VAT/GST/sales taxes unless exempt with valid documentation.'
            ]
          },
          {
            text: ['Payment processing may be performed by third-party processors. We do not typically store full payment card numbers.']
          }
        ]
      },
       {
        heading: '11. Cancellations',
        html: ['Cancellation rules depend on the purchased plan and are described at checkout and/or in our <a href="/refund">Refund Policy</a>']
      },
      {
        heading: '12. Suspension and Termination',
        blocks: [
          {
            text: ['We may suspend or terminate your account or a session if:'],
            list: [
              'You violate these Terms or AUP,',
              'Payment fails or is disputed,',
              'We detect suspected fraud or security risk,',
              'We are required to do so by law.'
            ]
          },
          {
            text: ['Termination may result in loss of access and deletion of your environments.']
          }
        ]
      },
      {
        heading: '13. Disclaimer of Warranties',
        text: ['The Service is provided “as is” and “as available.” We disclaim warranties to the extent permitted by law, including implied warranties of merchantability, fitness, and non-infringement.']
      },
       {
        heading: '14. Limitation of Liability',
        blocks: [
          {
            text: ['To the maximum extent permitted by law:'],
            list: [
              'We are not liable for indirect, incidental, special, consequential, or punitive damages,',
              'Our aggregate liability for claims related to the Service is limited to the amount you paid to us for the Service during the [e.g., previous 30 days / previous billing period] (choose and set later).'
            ]
          },
          {
            text: ['Some jurisdictions do not allow certain limitations, so they may not apply to you.']
          }
        ]
      },
        {
        heading: '15. Indemnification',
            text: ['You agree to indemnify and hold Elorimi harmless from claims arising from:'],
            list: [
              'Your Content,',
              'Your use of the Service,',
              'Your violation of these Terms or law.'
            ]
      },
         {
        heading: '16. Changes to the Terms',
            text: ['We may update these Terms. Continued use after changes means you accept the updated Terms. If changes are material, we may provide notice.']
      },
      {
        heading: '17. Contact',
        html: ['Questions about these Terms: <a href="mailto:info@elorimi.com">info@elorimi.com</a>']
      }
    ]
  },
  refund: {
    title: 'Refund Policy',
    updated: 'Last updated: February 8, 2026',
    sections: [
      {
        text: [
          'This Refund Policy explains when Elorimi may issue refunds for Cloud PC / Cloud VPS purchases.'
        ]
      },
        {
        heading: '1. General Principles',
        text: [
          'Because Elorimi provides time-based digital services and computing resources, refunds depend on whether the service was delivered and whether resources were reserved or consumed.'
        ]
      },
      {
        heading: '2. Eligibility for Refunds',
        text: [
          'You may be eligible for a full or partial refund if:'
        ],
        list: [
          'The Service was unavailable due to a verified issue on our side that prevented access for a significant portion of your paid session,',
          'You were charged incorrectly due to a billing error,',
          'You canceled a session within an allowed cancellation window (if offered at checkout).'
        ]
      },
        {
        heading: '3. Non-Refundable Situations',
        text: [
          'Refunds are generally not provided for:'
        ],
        list: [
          'Incorrect usage, user configuration issues, or user-installed software problems,',
          'Poor local internet connection, device limitations, or firewall restrictions on your side,',
          'Failure to connect because the credentials were shared or compromised on your side,',
          'Sessions that were used, even partially, unless otherwise required by law,',
          'Violations of the Terms or AUP (including fraud/abuse), which may result in termination without refund.'
        ]
      },
        {
        heading: '4. Partial Refunds / Credits',
        text: [
          'Where appropriate, we may issue:'
        ],
        list: [
          'A partial refund proportional to the verified downtime,',
          'Account credit toward future usage instead of a cash refund.'
        ]
      },
      {
        heading: '5. How to Request a Refund',
        blocks: [
          {
            html: ['To request a refund, contact support at <a href="mailto:info@elorimi.com">info@elorimi.com</a> and include:']
          },
          {
              list: [
              'Order ID / transaction reference,',
              'Date and time of the session,',
              'Description of the issue,',
              'Screenshots or error messages (if available).'
            ]
          }
        ]
      },
      {
        heading: '6. Review Time and Payment Method',
        text: [
          'We may ask for additional information to verify the issue. Approved refunds are returned to the original payment method when possible, or provided as credit, depending on payment processor limitations.'
        ]
      },
      {
        heading: '7. Chargebacks',
        text: [
          'If you initiate a chargeback without contacting us first, we may suspend your account during investigation. Fraudulent chargebacks may result in permanent termination.'
        ]
      },
      {
        heading: '8. Statutory Rights',
        text: [
          'Nothing in this policy limits any mandatory consumer rights you may have under applicable law.'
        ]
      }
    ]
  },
  shipping: {
    title: 'Shipping Policy',
    updated: 'Last updated: February 8, 2026',
    sections: [
      {
        text: [
          'Elorimi provides digital services only. We do not ship physical goods.'
        ]
      },
      {
        heading: '1. No Physical Shipping',
        list: [
          'There are no shipping fees.',
          'There are no physical deliveries.'
        ]
      },
      {
        heading: '2. Digital Delivery / Service Activation',
        text: ['Your Cloud PC / Cloud VPS access is delivered digitally via:'],
        list: [
          'Email and/or account dashboard notifications,',
          'Connection credentials provided before your scheduled session start time (if applicable).'
        ]
      },
      {
        heading: '3. Delivery Issues',
        text: ['If you did not receive access details:'],
        list: [
          'Check spam/junk folders,',
          'Verify the email address on your account,',
          'Contact support at info@elorimi.com with your order ID.'
        ]
      }
    ]
  },
  cookies: {
    title: 'Cookie Policy',
    updated: 'Last updated: February 8, 2026',
    sections: [
      {
         html: ['This Cookie Policy explains how Elorimi uses cookies and similar technologies on <a href="https://www.elorimi.com/">https://www.elorimi.com/</a>.']
      },
      {
        heading: '1. What Are Cookies?',
        text: [
          'Cookies are small text files stored on your device when you visit a website. Similar technologies include pixels, local storage, and SDKs.'
        ]
      },
      {
        heading: '2. Why We Use Cookies',
         text: ['We use cookies for:'],
        list: [
          'Essential functions: logins, security, session management, fraud prevention,',
          'Preferences: language, display settings,',
          'Analytics: understanding usage patterns to improve the Service,',
          'Marketing (if used): measuring ad performance and personalization (where permitted).'
        ]
      },
       {
        heading: '3. Types of Cookies We May Use',
        blocks: [
          {
            subheading: '3.1 Strictly Necessary Cookies',
            text: ['Required for the Website and Service to function (e.g., authentication, security). These cannot typically be disabled without breaking core functionality.']
          },
          {
            subheading: '3.2 Performance and Analytics Cookies',
            text: ['Help us understand how users interact with the Website (pages visited, time on site, errors). We use this to improve performance and reliability.']
          },
          {
            subheading: '3.3 Functionality Cookies',
            text: ['Remember choices such as language and preferences.']
          },
           {
            subheading: '3.4 Marketing Cookies (Optional)',
            text: ['If enabled, help measure marketing effectiveness and personalize advertising. We use them only where permitted and/or with your consent.']
          }
        ]
      },
      {
        heading: '4. Cookie Management',
        blocks: [
          {
            text: ['You can control cookies by:'],
            list: [
              'Using cookie banner preferences (if available),',
              'Adjusting browser settings to block or delete cookies,',
              'Using privacy tools or extensions.'
            ]
          },
          {
            text: ['Blocking essential cookies may prevent you from logging in or using core features.']
          }
        ]
      },
      {
        heading: '5. Third-Party Cookies',
        text: [
          'Some third parties (analytics, payment providers, embedded content) may place cookies. Their policies govern how they use data.'
        ]
      },
       {
        heading: '6. Do Not Track',
        text: [
          'Some browsers offer “Do Not Track.” There is no universal standard, and we may not respond to all signals. Where required, we comply with applicable consent rules.'
        ]
      },
       {
        heading: '7. Updates to This Cookie Policy',
        text: [
          'We may update this Cookie Policy from time to time. Changes will be posted on the Website.'
        ]
      },
      {
        heading: '8. Contact',
        html: ['For cookie/privacy questions: <a href="mailto:info@elorimi.com">info@elorimi.com</a>']
      }
    ]
  }
};

const LegalPage = ({ policyKey }) => {
  const policy = policies[policyKey];

  useEffect(() => {
    if (policy) {
      setPageMeta({
        title: `${policy.title} — Elorimi`,
        description: `${policy.title} for Elorimi services.`,
      });
    } else {
      setPageMeta({
        title: 'Legal — Elorimi',
        description: 'Legal policies for Elorimi.',
      });
    }
  }, [policy]);

  if (!policy) {
    return (
      <section className="policy-section">
        <div className="policy-container">
          <div className="policy-card glass">
            <h1 className="policy-title">Policy not found</h1>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="policy-section">
      <div className="policy-container">
        <div className="policy-card glass">
          <div className="policy-header">
            <span className="section-tag">Legal</span>
            <h1 className="policy-title">{policy.title}</h1>
            <p className="policy-updated">{policy.updated}</p>
          </div>

          <div className="policy-body">
            {policy.sections.map((section, idx) => (
              <div className="policy-block" key={idx}>
                <h2 className="policy-heading">{section.heading}</h2>
                {section.subheading && (
                  <h3 className="policy-subheading">{section.subheading}</h3>
                )}
                {section.subheadings && section.subheadings.map((sub, i) => (
                  <h3 className="policy-subheading" key={`sub-${i}`}>{sub}</h3>
                ))}
                {section.text && section.text.map((t, i) => (
                  <p className="policy-text" key={i}>{t}</p>
                ))}
                {section.html && section.html.map((t, i) => (
                  <p
                    className="policy-text"
                    key={`html-${i}`}
                    dangerouslySetInnerHTML={{ __html: t }}
                  />
                ))}
                {section.blocks && section.blocks.map((block, bIdx) => (
                  <div className="policy-subblock" key={`block-${bIdx}`}>
                    {block.subheading && (
                      <h3 className="policy-subheading">{block.subheading}</h3>
                    )}
                    {block.text && block.text.map((t, i) => (
                      <p className="policy-text" key={`block-text-${bIdx}-${i}`}>{t}</p>
                    ))}
                    {block.html && block.html.map((t, i) => (
                      <p
                        className="policy-text"
                        key={`block-html-${bIdx}-${i}`}
                        dangerouslySetInnerHTML={{ __html: t }}
                      />
                    ))}
                    {block.list && (
                      <ul className="policy-list">
                        {block.list.map((item, i) => (
                          <li key={`block-li-${bIdx}-${i}`}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {section.textAfter && section.textAfter.map((t, i) => (
                  <p className="policy-text" key={`after-${i}`}>{t}</p>
                ))}
                {section.list && (
                  <ul className="policy-list">
                    {section.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalPage;
